import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Check, ChevronDown } from 'lucide-react';

/**
 * Multi-select com lista de opções, busca, "Concluir", ESC, X clear.
 * Abre on-hover (delay 120ms) e fecha on-mouseleave (delay 350ms) para evitar
 * cascata acidental ao atravessar a barra de filtros e tolerar drift do cursor.
 * Click/teclado continuam funcionando como fallback.
 */
const ENTER_DELAY = 120;
const LEAVE_DELAY = 350;

const FilterPopover = ({
  icon: Icon,
  placeholder,
  options,
  value,
  onChange,
  compactNoun = 'sel.',
  searchPlaceholder = 'Buscar...',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);

  const valueSet = useMemo(() => new Set((value || []).map((v) => v.value)), [value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        e.stopPropagation();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(
    () => () => {
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
    },
    []
  );

  const cancelTimers = () => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    enterTimer.current = null;
    leaveTimer.current = null;
  };

  const handleMouseEnter = () => {
    if (disabled) return;
    clearTimeout(leaveTimer.current);
    leaveTimer.current = null;
    if (open) return;
    enterTimer.current = setTimeout(() => setOpen(true), ENTER_DELAY);
  };

  const handleMouseLeave = () => {
    clearTimeout(enterTimer.current);
    enterTimer.current = null;
    leaveTimer.current = setTimeout(() => setOpen(false), LEAVE_DELAY);
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    cancelTimers();
    setOpen((o) => !o);
  };

  const toggle = (opt) => {
    if (valueSet.has(opt.value)) {
      onChange((value || []).filter((v) => v.value !== opt.value));
    } else {
      onChange([...(value || []), opt]);
    }
  };

  const count = (value || []).length;
  const total = (options || []).length;
  let displayText;
  if (count === 0) displayText = placeholder;
  else if (total > 1 && count === total) displayText = 'Todos';
  else if (count === 1) displayText = value[0]?.label || '';
  else displayText = `${count} ${compactNoun}`;

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((o) => valueSet.has(o.value));

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredSet = new Set(filtered.map((f) => f.value));
      onChange((value || []).filter((v) => !filteredSet.has(v.value)));
    } else {
      const merged = [...(value || [])];
      filtered.forEach((opt) => {
        if (!valueSet.has(opt.value)) merged.push(opt);
      });
      onChange(merged);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="relative w-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={handleTriggerClick}
        className={`relative w-full pl-10 pr-12 py-2 border rounded-lg h-[42px] text-sm text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          count > 0
            ? 'border-blue-400 bg-blue-50/40 hover:bg-blue-50'
            : 'border-slate-300 bg-white hover:border-slate-400'
        }`}
      >
        {Icon && (
          <Icon
            size={18}
            className={`absolute left-3 top-[11px] ${
              count > 0 ? 'text-blue-500' : 'text-slate-400'
            }`}
          />
        )}
        <span
          className={`block truncate ${count === 0 ? 'text-slate-400' : 'text-slate-700 font-medium'}`}
        >
          {displayText}
        </span>
        <ChevronDown
          size={14}
          className={`absolute right-2 top-[14px] text-slate-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
        {count > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
              setQuery('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
                setQuery('');
              }
            }}
            className="absolute right-7 top-[12px] p-0.5 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded cursor-pointer"
            title="Limpar seleção"
          >
            <X size={14} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full bg-white border border-slate-200 rounded-lg rounded-t-none shadow-xl z-50 flex flex-col overflow-hidden -mt-px">
          <div className="p-2 border-b border-slate-100 bg-slate-50/40">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-[9px] text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                className="w-full pl-8 pr-2 py-1.5 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>

          {filtered.length > 1 && (
            <button
              type="button"
              onClick={handleSelectAll}
              className="px-3 py-1.5 text-[11px] text-blue-600 hover:bg-blue-50 text-left border-b border-slate-100"
            >
              {allFilteredSelected ? '✕ Desmarcar todos' : '✓ Selecionar todos'}
              {query && filtered.length !== total && (
                <span className="text-slate-400 ml-1">({filtered.length} filtrados)</span>
              )}
            </button>
          )}

          <div className="overflow-y-auto" style={{ maxHeight: '40vh' }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-xs text-slate-400 text-center italic">
                Nenhuma opção encontrada
              </div>
            ) : (
              filtered.map((opt) => {
                const active = valueSet.has(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer border-l-2 ${
                      active
                        ? 'border-blue-500 bg-blue-50/40 text-slate-800'
                        : 'border-transparent text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggle(opt)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="truncate flex-1">{opt.label}</span>
                  </label>
                );
              })
            )}
          </div>

          <div className="p-2 border-t border-slate-100 flex justify-between items-center gap-2 bg-slate-50/60">
            <button
              type="button"
              onClick={() => {
                onChange([]);
                setQuery('');
              }}
              disabled={count === 0}
              className="text-xs text-slate-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-slate-500"
            >
              Limpar
            </button>
            <span className="text-[10px] text-slate-400">
              {count} de {total} · ESC fecha
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
            >
              <Check size={12} /> Concluir
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPopover;
